import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { generateBreadcrumbSchema, generateOrganizationSchema } from '@/components/feature/SEOMeta';
import { useRef, useState, type FormEvent } from 'react';

const contactFormUrl = 'https://readdy.ai/api/form/d9th1r5ca23r0tr1aj00';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMessage(
          serverMsg.includes('spam') || serverMsg.includes('form data is spam')
            ? '提交失败，请稍后再试'
            : serverMsg
        );
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  return (
    <>
      <SEOMeta type="organization" data={{}} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <title>关于我们 - 乐球体育</title>
      <meta name="description" content="了解乐球体育的品牌故事、发展历程与核心价值观。乐球体育是您最值得信赖的专业体育平台，致力于为体育爱好者提供最优质的赛事资讯与分析服务。" />
      <meta name="keywords" content="乐球体育,关于乐球体育,乐球体育介绍,体育平台" />
      <link rel="canonical" href="https://aikaimanghe.cn/about" />

      <main>
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Hero */}
          <section className="py-10 md:py-16 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于乐球体育
            </h1>
            <p className="text-foreground-500 text-base max-w-2xl mx-auto leading-relaxed">
              乐球体育成立于2020年，是专业的体育赛事资讯平台。我们致力于为全球华语体育爱好者提供足球、篮球及综合体育领域的最新资讯、深度分析和实时数据服务。
            </p>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
                  <p>
                    乐球体育的创立源于创始团队对体育的无限热爱。2020年，一群来自互联网和体育行业的资深人士聚集在一起，怀揣着"让体育更有趣"的初心，创立了乐球体育平台。
                  </p>
                  <p>
                    从最初的小型体育资讯博客，到如今拥有50万+日活用户的专业体育平台，乐球体育始终坚持"专业、及时、深度"的内容理念。我们的编辑团队由资深体育记者、数据分析师和赛事评论员组成，确保每一条资讯都经过严格的专业审核。
                  </p>
                  <p>
                    乐球体育不仅是一个资讯平台，更是一个充满活力的体育社区。在这里，球迷们可以分享观点、参与讨论，共同享受体育带来的激情与快乐。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20open%20office%20space%20with%20sports%20branding%20elements%2C%20warm%20lighting%2C%20collaborative%20team%20environment%2C%20motivational%20sports%20posters%20on%20walls%2C%20clean%20minimal%20design%2C%20professional%20yet%20vibrant%20atmosphere%2C%20editorial%20photography%20style&width=800&height=533&seq=about-brand-story&orientation=landscape"
                  alt="乐球体育办公环境"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-10 md:py-14 bg-background-100 rounded-2xl px-4 md:px-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '专业可靠', desc: '严格的资讯审核流程，确保每一条内容的准确性和权威性' },
                { icon: 'ri-flashlight-line', title: '快速及时', desc: '7×24小时全天候更新，重大赛事实时推送，让您第一时间掌握赛况' },
                { icon: 'ri-bar-chart-box-line', title: '深度分析', desc: '专业的数据分析团队，为每一场赛事提供多维度的深度解读' },
                { icon: 'ri-group-line', title: '用户至上', desc: '以用户需求为核心，持续优化产品体验，打造最好的体育社区' },
              ].map((value, i) => (
                <div key={i} className="text-center p-5">
                  <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className={`${value.icon} text-primary-500 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="py-10 md:py-14">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
              行业声誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { title: '最佳体育资讯平台', org: '中国互联网协会', year: '2025' },
                { title: '用户最喜爱体育App', org: 'App Store年度评选', year: '2025' },
                { title: '最具影响力体育媒体', org: '体育产业峰会', year: '2024' },
              ].map((award, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-award-line text-accent-500 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-1">{award.title}</h3>
                  <p className="text-foreground-500 text-xs">{award.org}</p>
                  <span className="inline-block mt-2 text-xs text-accent-600 font-medium bg-accent-50 px-3 py-0.5 rounded-full">
                    {award.year}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-4">联系我们</h2>
                <p className="text-foreground-500 text-sm mb-6 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的客服团队将在24小时内回复您的咨询。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm text-foreground-800 font-medium">contact@leqiusports.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">客服电话</p>
                      <p className="text-sm text-foreground-800 font-medium">400-888-6688</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm text-foreground-800 font-medium">北京市朝阳区体育中心路88号乐球大厦18层</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">在线留言</h3>
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-white text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-white text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-white text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="visually-hidden"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                  </div>
                  {formStatus !== 'idle' && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${
                      formStatus === 'success'
                        ? 'bg-accent-50 text-accent-700 border border-accent-200'
                        : 'bg-primary-50 text-primary-700 border border-primary-200'
                    }`}>
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}